<template>
  <layout-block :class="baseStyle">
    <div v-if="props.TabModuleTag" class="mx-auto w-fit">
      <base-tag>
        {{ props.TabModuleTag }}
      </base-tag>
    </div>
    <div v-if="props.TabModuleHeadline" class="mx-auto mt-3 w-fit">
      <h2
        class="hyphens-auto text-center font-default text-h2-s font-normal sm:text-h2"
      >
        {{ props.TabModuleHeadline }}
      </h2>
    </div>
    <div v-if="props.TabModuleText" class="mx-auto mt-8 w-fit">
      <base-text
        class="max-w-3xl text-center text-default"
        :Text="props.TabModuleText"
      />
    </div>
    <div class="mx-auto mt-15 w-fit">
      <div
        class="grid overflow-hidden break-words rounded-full sm:gap-2 sm:p-2"
        :class="`${accentBackground} grid-cols-${props.TabModuleComponents.length}`"
      >
        <div
          v-for="(component, index) in props.TabModuleComponents"
          ref="buttonWrappers"
        >
          <base-button
            v-if="component"
            class="h-full w-full justify-center overflow-hidden rounded-full text-2xs transition-all sm:text-s md:text-default"
            :class="index === 0 ? activeButtonStyle : ''"
            :type="
              props.TabModuleVariation === 'dark' ? 'primary' : 'secondary'
            "
            :is-button="false"
            :transparent="true"
            @click="
              handleButtonClick(index);
              hasUserClicked = true;
            "
          >
            <div class="mx-1 sm:mx-4 sm:my-2">
              {{ component.ImageListCtaButtonText }}
            </div>
          </base-button>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 grid-rows-1 overflow-hidden">
      <div
        v-for="(component, index) in props.TabModuleComponents"
        ref="tabComponents"
        class="col-start-1 col-end-1 row-start-1 row-end-1 mt-6"
        :class="{ slideOut: index !== 0, slideIn: index === 0 }"
      >
        <div v-if="component">
          <block-image-list-cta
            v-bind="component"
            class="image"
            :outer-space="false"
          />
        </div>
      </div>
    </div>
  </layout-block>
</template>

<script setup lang="ts">
import type {
  ComponentBlockTabModuleFragment,
  Enum_Componentblocktabmodule_Variation,
} from "~~/types/graphql";

const props = defineProps<{
  TabModuleHeadline?: ComponentBlockTabModuleFragment["TabModuleHeadline"];
  TabModuleTag: ComponentBlockTabModuleFragment["TabModuleTag"];
  TabModuleText: ComponentBlockTabModuleFragment["TabModuleText"];
  TabModuleComponents: ComponentBlockTabModuleFragment["TabModuleComponents"];
  TabModuleVariation: Enum_Componentblocktabmodule_Variation;
}>();

for (const component of props.TabModuleComponents) {
  if (component) component.ImageListCtaVariation = props.TabModuleVariation;
}

const tabComponents = ref<null | HTMLElement[]>(null);
const buttonWrappers = ref<null | HTMLElement[]>(null);

const waitTimeBetweenAutoSlides = 6000;
let currentSlide = 0;
let hasUserClicked: boolean = false;
let interval: NodeJS.Timer | null = null;

function nextSlide() {
  if (!tabComponents?.value || !buttonWrappers?.value) return;

  currentSlide = (currentSlide + 1) % tabComponents.value.length;
  handleButtonClick(currentSlide);
}

onMounted(() => {
  if (
    props.TabModuleComponents?.length &&
    props.TabModuleComponents.length > 1
  ) {
    interval = setInterval(() => {
      if (!hasUserClicked) {
        nextSlide();
      } else if (interval) {
        clearInterval(interval);
      }
    }, waitTimeBetweenAutoSlides);
  }
});

onBeforeUnmount(() => {
  if (interval) clearInterval(interval);
});

const baseStyles = {
  light: ["bg-integer-white", "text-integer-black"],
  dark: ["bg-integer-black", "text-integer-white"],
};

const accentBackgrounds = {
  dark: "bg-light-black",
  light: "bg-light-gray",
};

const defaultButtonStyles = {
  light: ["bg-light-gray", "text-integer-black"],
  dark: ["bg-light-black", "text-integer-white"],
};

const activeButtonStyles = {
  light: ["text-integer-white", "bg-integer-black"],
  dark: ["text-integer-black", "bg-integer-white"],
};

const baseStyle = baseStyles[props.TabModuleVariation];
const defaultButtonStyle = defaultButtonStyles[props.TabModuleVariation];
const activeButtonStyle = activeButtonStyles[props.TabModuleVariation];
const accentBackground = accentBackgrounds[props.TabModuleVariation];

function setButtonStyleActive(wrapper: HTMLElement): void {
  const button = wrapper.children[0] as HTMLElement;

  for (const style of activeButtonStyle) {
    button.classList.add(style);
  }

  for (const style of defaultButtonStyle) {
    button.classList.remove(style);
  }
}

function setButtonStyleDefault(wrapper: HTMLElement): void {
  const button = wrapper.children[0] as HTMLElement;

  for (const style of defaultButtonStyle) {
    button.classList.add(style);
  }

  for (const style of activeButtonStyle) {
    button.classList.remove(style);
  }
}

function slideIn(component: HTMLElement): void {
  component.classList.add("slideIn");
  component.classList.remove("slideOut");
}

function slideOut(component: HTMLElement): void {
  component.classList.remove("slideIn");
  component.classList.add("slideOut");
}

function handleButtonClick(index: number): void {
  if (!tabComponents?.value || !buttonWrappers?.value) return;

  for (let i = 0; i < tabComponents.value.length; i++) {
    const component = tabComponents.value[i];
    if (i === index) slideIn(component);
    else slideOut(component);
  }

  for (let i = 0; i < buttonWrappers.value.length; i++) {
    const wrapper = buttonWrappers.value[i];
    if (i === index) setButtonStyleActive(wrapper);
    else setButtonStyleDefault(wrapper);
  }
}
</script>

<style scoped>
@keyframes slideIn {
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
  }
}

.slideIn {
  animation: slideIn 1s ease-in-out forwards;
}

.slideOut {
  animation: slideOut 1s ease-in-out forwards;
}

.break-words {
  word-break: break-word;
  overflow-wrap: break-word;
}

.image:deep(img) {
  @apply mx-auto max-h-screen-lg max-w-full md:mx-0;
}
</style>
